
import React from 'react';
import { UserProfile, Client } from '@/shared/types';

interface TemplateProps {
  profile: UserProfile;
  client?: Client | null;
  title: string;
  metaData: { label: string; value: string }[];
  children: React.ReactNode;
  footerNote?: string;
  type: string;
  containerClass: string;
  LogoImage: React.FC<{ size: 'sm' | 'md' | 'lg' | 'xl'; className?: string; light?: boolean }>;
  hideHeader?: boolean;
}

const ModernTemplate: React.FC<TemplateProps> = ({
  profile, client, title, metaData, children, footerNote, type, containerClass, LogoImage, hideHeader = false
}) => {
  return (
    <div className={containerClass}>
      {!hideHeader && (
        <>
          {/* Header Block */}
          <div className="bg-neutral-50 p-12 pb-24 relative overflow-hidden">
             {/* Decorative Elements */}
             <div style={{ backgroundColor: profile.brandColor, opacity: 0.05 }} className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full translate-x-1/3 -translate-y-1/2 pointer-events-none" />
             <div style={{ backgroundColor: profile.brandColor }} className="absolute top-0 left-0 w-2 h-full" />

             <div className="relative z-10 flex justify-between items-start">
                <div className="flex items-start gap-6">
                   <LogoImage size="md" />
                   <div className="pt-1">
                      <h1 className="text-2xl font-bold text-neutral-900 tracking-tight">{profile.name}</h1>
                      <div className="text-xs font-medium text-neutral-500 mt-2 space-y-1">
                         <p>{profile.address}</p>
                         <p>{profile.email}</p>
                         <p>{profile.phone}</p>
                      </div>
                   </div>
                </div>
                <div className="text-right">
                   <span style={{ backgroundColor: profile.brandColor }} className="px-5 py-2 rounded-lg text-white text-sm font-bold uppercase tracking-wider shadow-md">
                     {type}
                   </span>
                   <p className="mt-6 text-4xl font-bold text-neutral-900">{metaData.find(m => m.label.includes('#') || m.label.includes('Ref') || m.label === 'Quote Ref')?.value || '---'}</p>
                   <p className="text-neutral-500 font-medium mt-1">{title}</p>
                </div>
             </div>
          </div>

          {/* Info Strip (Floating Card Effect) */}
          <div className="px-12 -mt-12 relative z-20 mb-12">
             <div className="bg-white p-8 rounded-xl shadow-xl border border-neutral-100 grid grid-cols-2 gap-12">
                <div>
                   <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider mb-3">Billed To</p>
                   {client ? (
                     <div className="space-y-1">
                       <p className="font-bold text-neutral-900 text-lg">{client.name}</p>
                       <p className="text-sm font-medium text-neutral-500">{client.company}</p>
                       <p className="text-sm text-neutral-500">{client.address}</p>
                       <p className="text-sm text-neutral-500">{client.email}</p>
                     </div>
                   ) : <span className="text-neutral-300 italic">No Client Selected</span>}
                </div>
                <div className="grid grid-cols-2 gap-y-6 gap-x-4">
                   {metaData.filter(m => !m.label.includes('#') && !m.label.includes('Ref')).map((m, i) => (
                     <div key={i}>
                        <p className="text-xs font-bold text-neutral-400 uppercase mb-1">{m.label}</p>
                        <p className="font-bold text-neutral-800">{m.value}</p>
                     </div>
                   ))}
                </div>
             </div>
          </div>
        </>
      )}

      <div className="px-12 pb-12 flex-1">
         {children}
      </div>

      <div className="px-12 py-6 bg-neutral-900 text-white flex justify-between items-center mt-auto">
          <p className="text-sm opacity-80 font-medium">{footerNote || "Thank you for your partnership."}</p>
          <p className="font-bold opacity-40 text-sm tracking-widest uppercase">{profile.name}</p>
      </div>
    </div>
  );
};

export default ModernTemplate;
